import { useMemo, useState, type ReactNode } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'

import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import TextButton from 'LuxKit/Button/TextButton'
import JourneyV2Details from 'components/Flights/V2/FareFamily/JourneyV2Details'
import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import TripDrawerHeaderTitle from 'tripPlanner/components/GenericDrawer/TripDrawerHeaderTitle'
import { useRightPanelTripDrawerPresentation } from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import {
  type TransportOption,
  isLeFlightSavedItemData,
} from 'tripPlanner/api/types'
import { buildFlightName } from '../../utils/transportNames'
import type { BookedShortlistedFlightDetailsSection } from './types'

interface Props {
  isOpen: boolean
  option: TransportOption
  sections: Array<BookedShortlistedFlightDetailsSection>
  onClose: () => void
  onAfterClose?: () => void
}

const DrawerContent = styled(Group)`
  width: 100%;
  max-width: ${rem(700)};
  margin: ${rem(24)} auto;
  padding: 0 ${rem(24)};
  box-sizing: border-box;
`

interface SectionProps {
  section: BookedShortlistedFlightDetailsSection
  headingAccessory?: ReactNode
}

function Section({ section, headingAccessory }: SectionProps) {
  const [fareRulesURL, setFareRulesURL] = useState<string | undefined>(
    undefined,
  )
  const [fareRulesParams, setFareRulesParams] = useState<any>(undefined)

  return (
    <JourneyV2Details
      leg={section.leg}
      inclusions={section.inclusions}
      reservationInfo={section.reservationInfo}
      pnrId={section.pnrId}
      region={section.region}
      isStandalone={section.isStandalone}
      isDeparting={section.isDeparting}
      fareRulesURL={fareRulesURL}
      fareRulesParams={fareRulesParams}
      setFareRulesURL={setFareRulesURL}
      setFareRulesParams={setFareRulesParams}
      headingAccessory={headingAccessory}
    />
  )
}

function BookedShortlistedFlightDetailsDrawer({
  isOpen,
  option,
  sections,
  onClose,
  onAfterClose,
}: Props) {
  const intl = useIntl()
  const drawerPresentation = useRightPanelTripDrawerPresentation()

  const viewBookingLabel = intl.formatMessage({
    id: 'JRf6z08HHGKL4Undyqix',
    defaultMessage: 'View booking',
    description:
      'Primary action button in the booked shortlisted flight details drawer linking to My Escapes order view',
  })

  const drawerTitle = buildFlightName(
    option,
    intl.formatMessage({
      id: 'b9dYE4Xzwk-zhm5-Twgd',
      defaultMessage: 'Flight details',
      description:
        'Fallback title for a flight details drawer in the trip planner',
    }),
  )

  const savedItemData = isLeFlightSavedItemData(option.savedItemData)
    ? option.savedItemData
    : undefined
  const returnLegData = isLeFlightSavedItemData(option.returnLeg?.savedItemData)
    ? option.returnLeg.savedItemData
    : undefined
  const bookingOrderId = savedItemData?.orderId ?? returnLegData?.orderId
  const bookingUrl = bookingOrderId
    ? `/account/my-escapes/order/${bookingOrderId}`
    : undefined
  const bookingAction = useMemo(() => {
    if (!bookingUrl) {
      return undefined
    }

    return (
      <Group
        direction="horizontal"
        horizontalAlign="space-between"
        verticalAlign="end"
      >
        <Group direction="vertical" gap={4} />
        <TextButton
          kind="primary"
          size="large"
          href={bookingUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {viewBookingLabel}
        </TextButton>
      </Group>
    )
  }, [bookingUrl, viewBookingLabel])

  const content = useMemo(() => {
    if (sections.length <= 1) {
      return sections.map((section) => (
        <Section
          key={section.key}
          section={section}
          headingAccessory={bookingAction}
        />
      ))
    }

    return sections.flatMap((section, index) => {
      const node = (
        <Section
          key={section.key}
          section={section}
          headingAccessory={index === 0 ? bookingAction : undefined}
        />
      )
      if (index === 0) {
        return [node, <Divider key={`${section.key}-divider`} kind="primary" />]
      }
      return [node]
    })
  }, [bookingAction, sections])

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={onClose}
      onAfterClose={onAfterClose}
      direction="right-to-left"
      presentation={drawerPresentation}
      header={<TripDrawerHeaderTitle title={drawerTitle} />}
    >
      <DrawerContent direction="vertical" gap={24}>
        {content}
      </DrawerContent>
    </TripDrawer>
  )
}

export default BookedShortlistedFlightDetailsDrawer
